<script lang="ts">
    import { t } from 'svelte-i18n';
    import { createMutation } from '@tanstack/svelte-query';
    import type { ApiRequestParams } from '$lib/api/shopwareApi.js';
    import { ContactApi } from '$lib/api/ContactApi.js';
    import { toast, useContactFormData } from '$lib/stores.js';
    import Button from '$lib/daisyUiComponents/Button.svelte';
    import { PUBLIC_NOT_SPECIFIED_SALUTATION_ID } from '$env/static/public';
    import { useLocaleContext } from '$lib/context.js';

    const lang = useLocaleContext();

    const sendContactMailMutation = createMutation<void, unknown, ApiRequestParams<'sendContactMail'>>({
        mutationFn: (data) => ContactApi.sendContactMail(data)
    });

    const data = useContactFormData();

    async function sendContactMail(event: Event) {
        event.preventDefault();

        if (!$data.name || !$data.email || !$data.message) {
            $toast.showWarning($t('website.error.formIncomplete'));

            return;
        }

        if (!$data.acceptedTerms) {
            $toast.showWarning($t('website.error.termsNotAccepted'));

            return;
        }

        try {
            await $sendContactMailMutation.mutateAsync({
                salutationId: PUBLIC_NOT_SPECIFIED_SALUTATION_ID,
                firstName: '-',
                lastName: $data.name,
                phone: 'Not specified in contact formular',
                email: $data.email,
                subject: 'Contact formular',
                comment: $data.message
            });

            window.fbq?.('track', 'Contact');

            $data.name = '';
            $data.email = '';
            $data.message = '';

            $toast.showSuccess($t('website.footer.contactForm.submissionSucceeded'));
        } catch (err) {
            $toast.showError($t('website.footer.contactForm.submissionFailed'));
        }
    }
</script>

<div class="w-full lg:w-[50%]" id="contact">
    <h3 class="h3 white-stroke font-space-grotesk" style="color: black !important;">
        {$t('website.footer.contactForm.caption')}
    </h3>
    <form on:submit={sendContactMail} class="flex flex-col space-y-5 my-7">
        <input
            type="text"
            bind:value={$data.name}
            placeholder={$t('website.footer.contactForm.name')}
            class="bg-transparent placeholder:text-[#707070] border-b-2 text-[16px] border-b-primary py-2 text-white pl-1"
            autocomplete="name"
            disabled={$sendContactMailMutation.isLoading}
        />
        <input
            type="email"
            bind:value={$data.email}
            placeholder={$t('website.footer.contactForm.email')}
            class="bg-transparent placeholder:text-[#707070] border-b-2 text-[16px] border-b-primary py-2 text-white pl-1"
            autocomplete="email"
            disabled={$sendContactMailMutation.isLoading}
        />
        <textarea
            bind:value={$data.message}
            cols="30"
            rows="4"
            placeholder={$t('website.footer.contactForm.message')}
            class="bg-transparent placeholder:text-[#707070] border-b-2 text-[16px] border-b-primary py-2 text-white pl-1 mb-8"
            disabled={$sendContactMailMutation.isLoading}
        />
        <div class="form-control">
            <label class="label cursor-pointer flex justify-start items-start gap-2">
                <input type="checkbox" class="checkbox checkbox-primary mt-2" bind:checked={$data.acceptedTerms} />
                <span class="label-text">
                    {$t('website.footer.contactForm.privacy.0')}<a
                        class="text-primary transition-colors hover:!text-secondary"
                        href="/{lang}/privacy">{$t('website.footer.contactForm.privacy.1')}</a
                    >{$t('website.footer.contactForm.privacy.2')}<a
                        class="text-primary transition-colors hover:!text-secondary"
                        href="/{lang}/privacy">{$t('website.footer.contactForm.privacy.3')}</a
                    >{$t('website.footer.contactForm.privacy.4')}
                </span>
            </label>
        </div>
        <div>
            <Button
                submit
                btnStyle="ghost"
                class="text-[#E5E5E5] text-lg bg-primary uppercase hover:text-black hover:bg-primary"
                loading={$sendContactMailMutation.isLoading}
            >
                {$t('website.footer.contactForm.submit')}
            </Button>
        </div>
    </form>
</div>
